<template>
  <div class="my-orders-page container-fluid p-0">
    <div class="my-orders-inner">

      <!-- Header -->
      <div class="my-orders-header">
        <button class="btn-back" @click="$router.back()">
          <i class="fas fa-chevron-left"></i>
        </button>
        <div>
          <h1 class="page-title">Minhas Compras</h1>
          <p class="page-subtitle">Histórico de presentes enviados</p>
        </div>
      </div>

      <!-- Seletor de perfis -->
      <div v-if="customerStore.hasCustomers" class="profiles-section">
        <p class="section-label">Selecione o perfil</p>
        <div class="profiles-scroll">
          <button
            v-for="c in customerStore.customers"
            :key="c.cpf_cnpj"
            :class="['profile-chip', { active: selectedCustomer?.cpf_cnpj === c.cpf_cnpj }]"
            @click="selectCustomer(c)"
          >
            <span class="profile-chip-avatar">
              {{ c.name.charAt(0).toUpperCase() }}
            </span>
            <span class="profile-chip-name">{{ firstName(c.name) }}</span>
          </button>
        </div>
      </div>

      <!-- Sem perfis salvos -->
      <div v-else class="empty-state">
        <i class="fas fa-user-slash empty-icon"></i>
        <h4>Nenhum perfil salvo</h4>
        <p class="text-muted">Realize uma compra para que seus dados fiquem salvos aqui.</p>
        <router-link to="/" class="btn btn-color-site mt-3 px-4 py-2">
          <i class="fas fa-gift me-2"></i>Ver listas de presentes
        </router-link>
      </div>

      <!-- Pedidos do perfil selecionado -->
      <div v-if="selectedCustomer">

        <!-- Carregando -->
        <div v-if="loading" class="orders-loading">
          <div class="spinner-border text-pink" role="status"></div>
          <p class="text-muted mt-3">Buscando seus pedidos...</p>
        </div>

        <!-- Erro -->
        <div v-else-if="error" class="empty-state">
          <i class="fas fa-triangle-exclamation empty-icon text-warning"></i>
          <h4>Não foi possível carregar</h4>
          <p class="text-muted">{{ error }}</p>
          <button class="btn btn-color-site mt-3 px-4 py-2" @click="loadOrders">
            <i class="fas fa-rotate-right me-2"></i>Tentar novamente
          </button>
        </div>

        <!-- Lista de pedidos -->
        <div v-else-if="orders.length > 0" class="orders-list">
          <p class="section-label">Pedidos de {{ firstName(selectedCustomer.name) }}</p>
          <div
            v-for="order in orders"
            :key="order.id"
            class="order-card"
          >
            <div class="order-card-top">
              <div class="order-event-info">
                <div class="order-event-avatar">
                  <img v-if="order.event_photo" :src="order.event_photo" alt="" class="order-event-img" />
                  <i v-else class="fas fa-gift"></i>
                </div>
                <div class="order-event-text">
                  <span class="order-event-name">{{ order.event_name }}</span>
                  <span class="order-event-type">{{ order.event_type }}</span>
                </div>
              </div>
              <PaymentStatusBadge :status="order.status" />
            </div>

            <div class="order-card-body">
              <div class="order-items">
                <span v-for="(item, i) in order.items" :key="i" class="order-item-tag">
                  {{ item.quantity }}× {{ item.name }}
                </span>
              </div>
              <div class="order-card-footer">
                <span class="order-method">
                  <i :class="order.method === 'pix' ? 'fas fa-qrcode' : 'fas fa-credit-card'" class="me-1"></i>
                  {{ order.method === 'pix' ? 'Pix' : 'Cartão' }}
                </span>
                <span class="order-amount">
                  {{ formatCurrency(order.total) }}
                </span>
                <span class="order-date">{{ formatDate(order.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Sem pedidos -->
        <div v-else class="empty-state">
          <i class="fas fa-box-open empty-icon"></i>
          <h4>Nenhum pedido encontrado</h4>
          <p class="text-muted">{{ firstName(selectedCustomer.name) }} ainda não realizou compras.</p>
        </div>

      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { formatDateShort as formatDate } from '@/utils/dateFormatter';
import { useCustomerStore } from '@/stores/customerStore';
import api from '@/services/api';
import PaymentStatusBadge from '@/components/PaymentStatusBadge.vue';

const customerStore   = useCustomerStore();
const selectedCustomer = ref(null);
const orders           = ref([]);
const loading          = ref(false);
const error            = ref(null);

// Selecionar o perfil ativo por padrão, ou o primeiro da lista
onMounted(() => {
  if (customerStore.activeCustomer) {
    selectCustomer(customerStore.activeCustomer);
  } else if (customerStore.customers.length > 0) {
    selectCustomer(customerStore.customers[0]);
  }
});

function selectCustomer(c) {
  selectedCustomer.value = c;
  loadOrders();
}

async function loadOrders() {
  if (!selectedCustomer.value?.id) {
    orders.value = [];
    return;
  }
  loading.value = true;
  error.value   = null;
  try {
    const { data } = await api.get(`/guests/${selectedCustomer.value.id}/orders`);
    orders.value = data;
  } catch (err) {
    error.value = err.response?.data?.message || 'Erro ao carregar pedidos.';
  } finally {
    loading.value = false;
  }
}

// Helpers
function firstName(name) {
  return name?.split(' ')[0] || name;
}
</script>

<style scoped>
.my-orders-page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: 100%;
  width: 100%;
  min-width: 600px;
  overflow-x: hidden;
}

.my-orders-inner {
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
  padding: 1.5rem 1rem 3rem;
}

/* Header */
.my-orders-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1.75rem;
}

.btn-back {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid #dee2e6;
  background: #fff;
  color: #32355d;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}
.btn-back:hover {
  border-color: #ff50a3;
  color: #ff50a3;
}

.page-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #32355d;
  margin: 0 0 0.1rem;
}
.page-subtitle {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0;
}

/* Seletor de perfis */
.profiles-section {
  margin-bottom: 1.5rem;
}

.section-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #adb5bd;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.6rem;
}

.profiles-scroll {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
  -webkit-overflow-scrolling: touch;
}
.profiles-scroll::-webkit-scrollbar { display: none; }

.profile-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  border: 1.5px solid #e9ecef;
  border-radius: 99px;
  padding: 0.4rem 0.875rem 0.4rem 0.4rem;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s, background 0.2s;
  flex-shrink: 0;
}
.profile-chip:hover      { border-color: #ff50a3; }
.profile-chip.active     { border-color: #ff50a3; background: #fff0f7; }

.profile-chip-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #ff50a3;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.profile-chip-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #32355d;
}

/* Orders list */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.orders-loading {
  text-align: center;
  padding: 3rem 0;
}

.order-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.875rem;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.order-card:hover {
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
}

.order-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem 0.5rem;
  gap: 0.75rem;
}

.order-event-info {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
  flex: 1;
}

.order-event-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff50a3, #ff8cc8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.85rem;
  flex-shrink: 0;
  overflow: hidden;
}

.order-event-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.order-event-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.order-event-name {
  font-weight: 700;
  color: #32355d;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-event-type {
  font-size: 0.75rem;
  color: #6c757d;
}

/* Badges — delegado ao PaymentStatusBadge.vue */

/* Body */
.order-card-body {
  padding: 0.4rem 1rem 0.875rem;
  border-top: 1px solid #f1f3f5;
}

.order-items {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.6rem;
}

.order-item-tag {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 99px;
  font-size: 0.75rem;
  color: #495057;
  padding: 0.15rem 0.6rem;
}

.order-card-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.order-amount {
  font-weight: 700;
  color: #32355d;
  margin-left: auto;
}

.order-date {
  white-space: nowrap;
}

/* Empty */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

.empty-icon {
  font-size: 3rem;
  color: #dee2e6;
  display: block;
  margin-bottom: 1rem;
}

.empty-state h4 {
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.5rem;
}

@media (max-width: 767.98px) {
  .my-orders-page {
    min-width: inherit;
    transform: translateZ(0);
  }
}

/* Utilities */
.text-pink  { color: #ff50a3 !important; }
.spinner-border.text-pink { color: #ff50a3 !important; }

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  border-radius: 0.5rem;
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  transition: background 0.2s;
}
.btn-color-site:hover {
  background-color: #e6408f;
  color: #fff;
}
</style>
